import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useMutation } from './useMutation';
import { useToast } from './useToast';
import { useConfirm } from './useConfirm';
import type { Purchase } from '../types';
import type { ExtractedBL } from '../lib/pdfExtractor';
import {
  uploadPdfToStorage,
  callExtractPdfBase64,
  feedCatalogFromBL,
  type BLLineForCatalog,
} from '../lib/catalogFeedingService';
import { getToday } from '../utils/dateUtils';
import * as XLSX from 'xlsx';
import { emptyLine, type ExtractionResult, type Supplier, type usePurchasesData } from './usePurchasesData';

type Data = ReturnType<typeof usePurchasesData>;

export function usePurchasesActions(data: Data) {
  const { user } = useAuth();
  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();

  const {
    formData, setFormData,
    formLines, setFormLines,
    setSaving,
    setCatalogFeedResult,
    pdfFiles, setPdfFiles,
    setExtracting,
    extractionResults, setExtractionResults,
    activeResultIndex, setActiveResultIndex,
    setDragOver,
    fileInputRef,
    currentPdfFile, setCurrentPdfFile,
    setShowForm,
    suppliers,
    refreshSuppliers,
    refreshPurchases,
    fetchData,
    expandedId, setExpandedId,
    editingId, setEditingId,
    editData, setEditData,
    editLines, setEditLines,
    newSupplier, setNewSupplier,
    filtered,
    dateStart, dateEnd,
  } = data;

  // PDF : GESTION DU DROP
  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files).filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    if (files.length > 0) setPdfFiles(prev => [...prev, ...files]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files).filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
      if (files.length > 0) setPdfFiles(prev => [...prev, ...files]);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePdfFile = (index: number) => {
    setPdfFiles(prev => prev.filter((_, i) => i !== index));
  };

  // PDF : MATCHER UN FOURNISSEUR
  const matchSupplier = (name: string): Supplier | null => {
    if (!name) return null;
    const normalised = name.toUpperCase().trim();
    for (const s of suppliers) {
      if (s.name.toUpperCase().includes(normalised) || normalised.includes(s.name.toUpperCase())) {
        return s;
      }
    }
    const words = normalised.split(/\s+/);
    for (const s of suppliers) {
      const sWords = s.name.toUpperCase().split(/\s+/);
      for (const w of words) {
        if (w.length >= 3 && sWords.some(sw => sw.includes(w) || w.includes(sw))) {
          return s;
        }
      }
    }
    return null;
  };

  // PDF : PRÉ-REMPLIR LE FORMULAIRE
  const prefillForm = async (bl: ExtractedBL, pdfFile?: File, resolvedSupplierId?: string | null) => {
    let supplierIdToUse = '';
    if (resolvedSupplierId) {
      supplierIdToUse = resolvedSupplierId;
    } else {
      let matchedSupplier = matchSupplier(bl.supplier_name);
      if (!matchedSupplier && bl.supplier_name) {
        const { data: newSup } = await supabase
          .from('suppliers')
          .insert({ name: bl.supplier_name })
          .select()
          .single();
        if (newSup) {
          await refreshSuppliers();
          matchedSupplier = newSup as Supplier;
          console.log(`[PDF] Fournisseur créé automatiquement : ${bl.supplier_name}`);
        }
      }
      supplierIdToUse = matchedSupplier?.id || '';
    }

    let matchedProjectId = '';
    if (bl.project_code) {
      const codeRaw = bl.project_code.trim().toUpperCase();
      const { data: matchedProject } = await supabase
        .from('projects')
        .select('id, code, name')
        .ilike('code', codeRaw)
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (matchedProject) {
        matchedProjectId = matchedProject.id;
        console.log(`[PDF] Affaire trouvée : ${matchedProject.name} (${matchedProject.code})`);
      } else {
        console.log(`[PDF] Code affaire "${codeRaw}" non trouvé dans Supabase`);
      }
    }

    setFormData({
      project_id: matchedProjectId,
      supplier_id: supplierIdToUse,
      bl_number: bl.bl_number || '',
      bl_date: bl.bl_date || getToday(),
      notes: bl.supplier_name ? `Fournisseur détecté : ${bl.supplier_name}` : ''
    });
    setFormLines(
      bl.lines.length > 0
        ? bl.lines.map(l => ({
            reference: l.reference,
            designation: l.designation,
            quantity: l.quantity,
            unit: (l.unit || 'pce').toLowerCase(),
            unit_price: l.unit_price
          }))
        : [emptyLine()]
    );
    if (pdfFile) setCurrentPdfFile(pdfFile);
    setCatalogFeedResult(null);
    setShowForm(true);
  };

  // PDF : Convertir un File en base64
  const fileToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        resolve(dataUrl.split(',')[1]);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  // PDF : LANCER L'EXTRACTION
  const handleExtract = async () => {
    if (pdfFiles.length === 0 || !user?.id) return;
    setExtracting(true);
    setExtractionResults([]);

    const promises = pdfFiles.map(async (file): Promise<ExtractionResult> => {
      try {
        const pdfBase64 = await fileToBase64(file);

        const [uploadResult, edgeResult] = await Promise.all([
          uploadPdfToStorage(file, user.id),
          callExtractPdfBase64(pdfBase64, true)
        ]);

        if (!edgeResult?.success || !edgeResult.extracted) {
          return {
            file, result: null,
            error: edgeResult?.error || 'Extraction OCR échouée — vérifiez que la clé API Mistral est configurée.',
            _uploadResult: uploadResult, _resolvedSupplierId: null
          };
        }

        const ext = edgeResult.extracted;
        const mappedResult: ExtractedBL = {
          supplier_name: ext.supplier_name || '',
          bl_number: ext.bl_number || '',
          bl_date: ext.bl_date || '',
          lines: (ext.lines || []).map(l => ({
            reference: l.reference || '',
            designation: l.designation || '',
            quantity: l.quantity || 0,
            unit: l.unit || 'PCE',
            unit_price: l.unit_price || 0,
          })),
          total_ht: ext.total_ht ?? undefined,
          project_code: ext.project_code || undefined,
          raw_text: '',
        };

        return {
          file, result: mappedResult, error: null,
          _uploadResult: uploadResult,
          _resolvedSupplierId: edgeResult.supplier_id_resolved || null,
        };
      } catch (err: unknown) {
        console.error(`[PDF] Erreur extraction "${file.name}":`, err);
        return { file, result: null, error: err instanceof Error ? err.message : 'Erreur inconnue', _uploadResult: null, _resolvedSupplierId: null };
      }
    });

    const results = await Promise.all(promises);

    setExtractionResults(results);
    setActiveResultIndex(0);
    setExtracting(false);

    const firstValid = results.find(r => r.result !== null);
    if (firstValid?.result) {
      prefillForm(firstValid.result, firstValid.file, firstValid._resolvedSupplierId);
    }
  };

  // PDF : PASSER AU RÉSULTAT SUIVANT
  const advanceToNextPdf = () => {
    const nextIndex = extractionResults.findIndex(
      (r, i) => i > activeResultIndex && r.result !== null
    );
    if (nextIndex >= 0) {
      setActiveResultIndex(nextIndex);
      const nextResult = extractionResults[nextIndex];
      prefillForm(nextResult.result!, nextResult.file, nextResult._resolvedSupplierId);
    } else {
      setPdfFiles([]);
      setExtractionResults([]);
      setActiveResultIndex(0);
      setShowForm(false);
    }
  };

  // CRÉER UN BL
  const handleCreate = async () => {
    if (!formData.project_id || !formData.supplier_id) return;
    if (formLines.filter(l => l.designation).length === 0) return;
    setSaving(true);
    setCatalogFeedResult(null);
    try {
      const validLines = formLines.filter(l => l.designation);

      const activeResult = extractionResults[activeResultIndex];
      const existingUpload = activeResult?._uploadResult;

      const uploadPromise = (!existingUpload && currentPdfFile && user?.id)
        ? uploadPdfToStorage(currentPdfFile, user.id)
        : Promise.resolve(existingUpload || null);

      const uploadResult = await uploadPromise;
      const pdfUrl = uploadResult?.publicUrl ?? null;

      const { data: newPurchase, error } = await supabase
        .from('purchases')
        .insert({
          project_id: formData.project_id,
          supplier_id: formData.supplier_id,
          bl_number: formData.bl_number || null,
          bl_date: formData.bl_date,
          notes: formData.notes || null,
          created_by: user?.id,
          status: 'draft',
          pdf_url: pdfUrl
        })
        .select()
        .single();

      if (error) throw error;

      const linesToInsert = validLines.map(l => ({
        purchase_id: newPurchase.id,
        material_id: null as string | null,
        reference: l.reference || null,
        designation: l.designation!,
        quantity: l.quantity || 1,
        unit: l.unit || 'pce',
        unit_price: l.unit_price || 0
      }));

      if (linesToInsert.length > 0) {
        const { error: lineError } = await supabase.from('purchase_lines').insert(linesToInsert);
        if (lineError) throw lineError;
      }

      toast.success('Bon de livraison créé.');
      setCurrentPdfFile(null);
      if (extractionResults.length > 0) {
        advanceToNextPdf();
      } else {
        setShowForm(false);
      }
      setFormData({ project_id: '', supplier_id: '', bl_number: '', bl_date: getToday(), notes: '' });
      setFormLines([emptyLine()]);
      fetchData();

      const blLines: BLLineForCatalog[] = validLines
        .filter(l => l.reference)
        .map(l => ({
          reference: l.reference!,
          designation: l.designation!,
          quantity: l.quantity || 1,
          unit: l.unit || 'pce',
          unit_price: l.unit_price || 0
        }));

      if (blLines.length > 0) {
        feedCatalogFromBL(formData.supplier_id, blLines).then(feedResult => {
          setCatalogFeedResult(feedResult);

          const updates = [...feedResult.materialIdMap.entries()]
            .map(([ref, matId]) => {
              const matchingLine = linesToInsert.find(
                l => l.reference?.trim().toUpperCase() === ref
              );
              return matchingLine ? { purchase_id: newPurchase.id, reference: matchingLine.reference, material_id: matId } : null;
            })
            .filter((u): u is { purchase_id: string; reference: string | null; material_id: string } => u !== null);

          if (updates.length > 0) {
            Promise.all(
              updates.map(u =>
                supabase
                  .from('purchase_lines')
                  .update({ material_id: u.material_id })
                  .eq('purchase_id', u.purchase_id)
                  .eq('reference', u.reference)
              )
            );
          }
        }).catch(err => console.error('[Catalog] Erreur alimentation catalogue:', err));
      }

    } catch (err) {
      console.error('Erreur création BL:', err);
      toast.error('Erreur lors de la création du bon de livraison');
    }
    setSaving(false);
  };

  // ÉDITION
  const startEdit = async (p: Purchase) => {
    setEditingId(p.id);
    setEditData({ bl_number: p.bl_number || '', notes: p.notes || '', status: p.status });
    const { data: lineData } = await supabase.from('purchase_lines').select('*').eq('purchase_id', p.id).order('created_at');
    setEditLines(lineData || []);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    setSaving(true);
    try {
      await supabase
        .from('purchases')
        .update({ bl_number: editData.bl_number || null, notes: editData.notes || null, status: editData.status, updated_at: new Date().toISOString() })
        .eq('id', editingId);

      await supabase.from('purchase_lines').delete().eq('purchase_id', editingId);
      const linesToInsert = editLines
        .filter(l => l.designation)
        .map(l => ({
          purchase_id: editingId,
          reference: l.reference || null,
          designation: l.designation!,
          quantity: l.quantity || 1,
          unit: l.unit || 'pce',
          unit_price: l.unit_price || 0
        }));
      if (linesToInsert.length > 0) {
        await supabase.from('purchase_lines').insert(linesToInsert);
      }

      toast.success('Bon de livraison modifié.');
      setEditingId(null);
      setExpandedId(null);
      fetchData();
    } catch (err) {
      console.error('Erreur édition BL:', err);
      toast.error('Erreur lors de la modification');
    }
    setSaving(false);
  };

  // SUPPRIMER
  const handleDelete = async (id: string) => {
    const ok = await confirmDialog({ title: "Supprimer ce bon de livraison ?", message: "Cette action est irréversible.", variant: "danger" });
    if (!ok) return;
    const { error: linesErr } = await supabase.from('purchase_lines').delete().eq('purchase_id', id);
    if (linesErr) { toast.error('Erreur : ' + linesErr.message); return; }
    const { error: purchaseErr } = await supabase.from('purchases').delete().eq('id', id);
    if (purchaseErr) { toast.error('Erreur : ' + purchaseErr.message); return; }
    toast.success('Bon de livraison supprimé.');
    if (expandedId === id) setExpandedId(null);
    fetchData();
  };

  // VALIDER
  const { execute: executeValidate } = useMutation(
    (id: string) => supabase.from('purchases').update({ status: 'validated', updated_at: new Date().toISOString() }).eq('id', id),
    { onSuccess: refreshPurchases }
  );
  const handleValidate = async (id: string) => {
    await executeValidate(id);
    toast.success("Bon de livraison validé");
  };

  // CRUD FOURNISSEURS
  const { execute: executeAddSupplier } = useMutation(
    (payload: { name: string; contact_name: string | null; phone: string | null; category: string | null }) =>
      supabase.from('suppliers').insert(payload),
    { onSuccess: refreshSuppliers }
  );
  const addSupplier = async () => {
    if (!newSupplier.name.trim()) return;
    const ok = await executeAddSupplier({
      name: newSupplier.name.trim(),
      contact_name: newSupplier.contact_name.trim() || null,
      phone: newSupplier.phone.trim() || null,
      category: newSupplier.category.trim() || null
    });
    if (ok) {
      toast.success('Fournisseur créé.');
      setNewSupplier({ name: '', contact_name: '', phone: '', category: '' });
    }
  };

  const { execute: executeDeleteSupplier } = useMutation(
    (id: string) => supabase.from('suppliers').delete().eq('id', id),
    { onSuccess: refreshSuppliers }
  );
  const deleteSupplier = async (id: string) => {
    const ok = await confirmDialog({ title: "Supprimer ce fournisseur ?", message: "Cette action est irréversible.", variant: "danger" });
    if (!ok) return;
    const deleted = await executeDeleteSupplier(id);
    if (deleted) toast.success('Fournisseur supprimé.');
  };

  // EXPORT EXCEL
  const exportExcel = () => {
    const rows = filtered.map(p => ({
      'Date BL': p.bl_date,
      'N° BL': p.bl_number || '',
      'Affaire': p.project?.code || '',
      'Affaire (nom)': p.project?.name || '',
      'Fournisseur': p.supplier?.name || '',
      'Total HT (€)': p.total_ht,
      'Statut': p.status === 'validated' ? 'Validé' : p.status === 'cancelled' ? 'Annulé' : 'Brouillon',
      'Notes': p.notes || ''
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Achats');
    XLSX.writeFile(wb, `achats_${dateStart}_${dateEnd}.xlsx`);
  };

  return {
    handleFileDrop,
    handleFileSelect,
    removePdfFile,
    matchSupplier,
    prefillForm,
    handleExtract,
    advanceToNextPdf,
    handleCreate,
    startEdit,
    saveEdit,
    handleDelete,
    handleValidate,
    addSupplier,
    deleteSupplier,
    exportExcel,
  };
}
